import React, { useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axios/axiosInstance';
import { Country, State, District, Taluk, City, EntityType } from '../types';
import LocationForm from './LocationForm';
import CustomModal from '../../../components/modal/CustomModal';

interface LocationListProps {
  entityType: EntityType;
  data: (Country | State | District | Taluk | City)[];
  onRefresh: () => void;
  parentOptions?: { id: number; name: string }[];
  parentLabel?: string;
  parentFilter?: number | null;
  onParentFilterChange?: (id: number | null) => void;
}

const LocationList: React.FC<LocationListProps> = ({
  entityType,
  data,
  onRefresh,
  parentOptions = [],
  parentLabel = '',
  parentFilter = null,
  onParentFilterChange,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState<Country | State | District | Taluk | City | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);

  const handleOpenForm = (item?: Country | State | District | Taluk | City) => {
    setEditData(item || null);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditData(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(`Are you sure you want to delete this ${entityType}?`)) {
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.delete(`/api/${entityType}/${id}`);
      toast.success(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} deleted successfully`);
      onRefresh();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || `Failed to delete ${entityType}`);
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const entityLabel = entityType.charAt(0).toUpperCase() + entityType.slice(1);
  // Data is already filtered by the API, so use it directly
  const filteredData = data;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {entityLabel}s
          </h3>
          <button
            onClick={() => handleOpenForm()}
            className="btn-primary flex items-center gap-2 px-4 py-2"
          >
            <i className="fa-solid fa-plus"></i>
            Add {entityLabel}
          </button>
        </div>

        {parentOptions.length > 0 && onParentFilterChange && (
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Filter by {parentLabel}
            </label>
            <select
              value={parentFilter || ''}
              onChange={(e) => onParentFilterChange(e.target.value ? Number(e.target.value) : null)}
              className="dark-input w-full md:w-auto px-4 py-2.5"
            >
              <option value="">All {parentLabel}s</option>
              {parentOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        {filteredData.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fa-solid fa-inbox text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              No {entityLabel.toLowerCase()}s found
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                  Code
                </th>
                {entityType !== 'country' && (
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                    {parentLabel}
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-300 tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {filteredData.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {item.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {item.code || '-'}
                  </td>
                  {entityType !== 'country' && (
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      {entityType === 'state' && (item as State).country?.name}
                      {entityType === 'district' && (item as District).state?.name}
                      {entityType === 'taluk' && (item as Taluk).district?.name}
                      {entityType === 'city' && (item as City).taluk?.name}
                    </td>
                  )}
                  <td className="px-6 py-4 text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        item.is_deleted
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      }`}
                    >
                      {item.is_deleted ? 'Deleted' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenForm(item)}
                        className="p-2 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                        title="Edit"
                      >
                        <i className="fa-solid fa-pen"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        disabled={loading && deleteId === item.id}
                        className="p-2 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                        title="Delete"
                      >
                        {loading && deleteId === item.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-red-600"></div>
                        ) : (
                          <i className="fa-solid fa-trash"></i>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <CustomModal open={isFormOpen} onClose={handleCloseForm}>
        <LocationForm
          entityType={entityType}
          onClose={handleCloseForm}
          onSuccess={onRefresh}
          editData={editData}
          parentOptions={parentOptions}
          parentLabel={parentLabel}
        />
      </CustomModal>
    </div>
  );
};

export default LocationList;

